import {APIService} from "../../common/APIService";
import {Quote} from "../../dto/Quote";
import moment from "moment/moment";
import {QuoteRow} from "../../dto/QuoteRow";
import {QuoteAttachmentTemplate} from "../../dto/QuoteAttachmentTemplate";
import {QuoteRowTemplate} from "../../dto/QuoteRowTemplate";

export class QuoteService extends APIService {
  public async browse() {
    const json = await this.getJson<{data: object[]}>(`quotes?withArchived=true`)
    return json.data.map(item => Quote.fromJson(item))
  }

  public async read(quote: string) {
    const json = await this.getJson<{data: object[]}>(`quotes/${quote}`)
    return Quote.fromJson(json.data)
  }

  public async add(relationId: string, projectId: string, contactId: string, reference: string, paymentTerm: number, description: string, memo: string) {
    const json = await this.postJson<{data: object[]}>(`quotes`, {
      project_id: projectId,
      relation_id: relationId,
      contact_id: contactId,
      reference: reference,
      payment_term: paymentTerm,
      description: description,
      memo: memo
    })
    return Quote.fromJson(json.data)
  }

  public async edit(quoteId: string, relationId: string|undefined, projectId: string|undefined, contactId: string|null, paymentTerm: number, expirationDate: Date|null, reference: string, status: string, description: string, paymentTerms: string|null) {
    const json = await this.putJson<{data: object[]}>(`quotes/${quoteId}`, {
      contact_id: contactId,
      payment_term: paymentTerm,
      expiration_date: expirationDate === null ? null : moment(expirationDate).format('YYYY-MM-DD'),
      reference: reference,
      status: status,
      description: description,
      ...(paymentTerms !== null ? {payment_terms: paymentTerms} : {}),

      // Conditionally add the project and relation to the update call,
      // this endpoint is shared between the quote details and quote
      // relation forms.
      ...(projectId !== undefined && { project_id: projectId }),
      ...(relationId !== undefined && { relation_id: relationId }),
    })
    return Quote.fromJson(json.data)
  }

  public async send(quoteId: string, toEmail: string, subject: string, text: string, toEmailCC?: string[]) {
    const json = await this.putJson<{data: object[]}>(`quotes/${quoteId}/send`, {
      email_to: toEmail, email_subject: subject, email_body: text,
      ...((toEmailCC !== undefined && toEmailCC.length > 0) ? {email_cc: toEmailCC} : {}),
    })
    return Quote.fromJson(json.data)
  }
  public async sendBulk(quotes: string[], emails: string[], subject: string, text: string) {
    await this.postJson<{data: object[]}>(`quotes/send`, {
      quote_ids: quotes,
      emails: emails,
      email_subject: subject,
      email_body: text,
    })
  }

  public async sendConfirmation(quoteId: string, toEmail: string, subject: string, text: string, toEmailCC?: string) {
    const json = await this.putJson<{data: object[]}>(`quotes/${quoteId}/send-confirmation-pdf`, {
      email_to: toEmail, email_subject: subject, email_body: text,
      ...((toEmailCC !== undefined && toEmailCC != "") ? {email_cc: toEmailCC} : {}),
    })
    return Quote.fromJson(json.data)
  }

  public async accept(quoteId: string) {
    const json = await this.putJson<{data: object[]}>(`quotes/${quoteId}/accept`, {})
    return Quote.fromJson(json.data)
  }

  public async reject(quoteId: string) {
    const json = await this.putJson<{data: object[]}>(`quotes/${quoteId}/reject`, {})
    return Quote.fromJson(json.data)
  }

  public async clone(quoteId: string, relationId?: string, contactId?: string|null, paymentTerm?: number, selectedOrderIds?: string[]) {
    const json = await this.postJson<{data: object[]}>(`quotes/${quoteId}/clone`, {
      ...(relationId !== undefined ? { relation_id: relationId } : {}),
      ...(contactId !== undefined ? { contact_id: contactId } : {}),
      ...(paymentTerm !== undefined ? { payment_term: paymentTerm } : {}),
      ...(selectedOrderIds !== undefined && selectedOrderIds.length > 0 ? {order_ids: selectedOrderIds} : {}),
    })
    return Quote.fromJson(json.data)
  }

  public async revision(quoteId: string) {
    const json = await this.postJson<{data: object[]}>(`quotes/${quoteId}/revision`, {})
    return Quote.fromJson(json.data)
  }

  public async archive(quoteId: string) {
    await this.deleteJson(`quotes/${quoteId}/archive`)
  }

  public async unarchive(quoteId: string) {
    await this.putJson(`quotes/${quoteId}/unarchive`, {})
  }

  public async downloadConfirmation(quoteId: string) {
    const response = await this.getRaw(`quotes/${quoteId}/download-confirmation-pdf`)
    return response.blob()
  }
  public async download(quoteId: string) {
    const response = await this.getRaw(`quotes/${quoteId}/download`)
    return response.blob()
  }
  public async preview(templateRowId: string) {
    const response = await this.getRaw(`quotes/63eae9f9-4ed9-4fe2-b7f9-82cb090498ea/${templateRowId}/preview`)
    return response.blob()
  }

  //////////
  // Rows //
  //////////
  public async addRow(quoteId: string, description: string, amount: number, price: number, ledger: string, vatRateId: string, templateId: string|null) {
    const json = await this.postJson<{data: object[]}>(`quotes/${quoteId}/rows`, {
      amount: amount,
      price: price,
      text: description,
      ledger_code_id: ledger,
      vat_rate_id: vatRateId,
      ...(templateId ? { template_id: templateId } : {}),
    })
    return QuoteRow.fromJson(json.data)
  }

  public async editRow(quoteId: string, rowId: string, description: string, amount: number, price: number, ledger: string, vatRateId: string) {
    const json = await this.putJson<{data: object[]}>(`quotes/${quoteId}/rows/${rowId}`, {
      amount: amount,
      price: price,
      text: description,
      ledger_code_id: ledger,
      vat_rate_id: vatRateId,
    })
    return QuoteRow.fromJson(json.data)
  }

  public async deleteRow(quoteId: string, quoteRowId: string) {
    await this.deleteJson(`quotes/${quoteId}/rows/${quoteRowId}`)
  }

  ////////////////////
  // Rows Templates //
  ////////////////////
  public async browseRowTemplates() {
    const json = await this.getJson<{data: object[]}>(`quotes/row-templates`)
    return json.data.map(item => QuoteRowTemplate.fromJson(item))
  }
  public async addRowTemplate(name: string, description: string, amount: number, price: number, ledgerId: string, vatRateId: string, includedAttachments: string[]) {
    const json = await this.postJson<{ data: object[] }>(`quotes/row-templates`, {
      amount: amount,
      price: price,
      name: name,
      text: description,
      ledger_code_id: ledgerId,
      vat_rate_id: vatRateId,
      included_attachments: includedAttachments,
    })
    return QuoteRowTemplate.fromJson(json.data)
  }
  public async editRowTemplate(rowTemplateId: string, name: string, description: string, amount: number, price: number, ledgerId: string, vatRateId: string, includedAttachments: string[]) {
    const json = await this.putJson<{ data: object[] }>(`quotes/row-templates/${rowTemplateId}`, {
      amount: amount,
      price: price,
      name: name,
      text: description,
      ledger_code_id: ledgerId,
      vat_rate_id: vatRateId,
      included_attachments: includedAttachments,
    })
    return QuoteRowTemplate.fromJson(json.data)
  }
  public async deleteRowTemplate(rowId: string) {
    await this.deleteJson(`quotes/row-templates/${rowId}`)
  }

  /////////////////
  // Attachments //
  /////////////////
  public async addAttachmentTemplatesToQuote(quoteId: string, templateIds: string[]) {
    const json = await this.postJson(`quotes/${quoteId}/attachments`, {
      template_ids: templateIds
    })
  }
  public async addNewAttachmentToQuote(quoteId: string, files: File[]) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files[]', file, file.name)
    })
    await this.postForm(`quotes/${quoteId}/attachments`, formData)
  }
  public async deleteAttachment(quoteId: string, quoteAttachmentId: string) {
    await this.deleteJson(`quotes/${quoteId}/attachments/${quoteAttachmentId}`)
  }

  ///////////////////////////
  // Attachments Templates //
  ///////////////////////////
  public async browseAttachmentTemplates() {
    const json = await this.getJson<{ data: object[] }>(`quotes/attachment-templates`)
    return json.data.map(item => QuoteAttachmentTemplate.fromJson(item))
  }
  public async addAttachmentTemplates(files: File[]) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files[]', file, file.name)
    })
    await this.postForm(`quotes/attachment-templates`, formData)
  }
  public async renameAttachmentTemplate(templateId: string, name: string) {
    const json = await this.putJson<{ data: object[] }>(`quotes/attachment-templates/${templateId}`, {
      filename: name
    })
    return Quote.fromJson(json.data)
  }
  public async downloadAttachmentTemplate(templateId: string): Promise<Blob> {
    const response = await this.getRaw(`quotes/attachment-templates/${templateId}/download`)
    return await response.blob()
  }
  public async deleteAttachmentTemplate(templateId: string) {
    await this.deleteJson(`quotes/attachment-templates/${templateId}`)
  }
}
